import { useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { describeSearchOrigin, SearchOrigin } from "@pages/AdminDashboard/accountFromSearch";
import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { Button } from "@pages/AdminDashboard/ui/Button";
import { Key } from "@pages/AdminDashboard/ui/Key";

const { color, typeScale } = adminTokens;
const [, , spaceMd] = adminTokens.spacing;
// The design's bar: 14px corners, padded 10px above and below, 12px at the right and 18px at
// the left; its words take at least 240px before the Button wraps below them.
const barRadius = 14;
const barPadding = "10px 12px 10px 18px";
const wordsMinWidth = 240;

const Bar = styled.section<{ $isPhone: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: ${({ $isPhone }) => ($isPhone ? "stretch" : "center")};
  gap: ${spaceMd}px;
  padding: ${barPadding};
  border-radius: ${barRadius}px;
  background-color: ${color.canvas};
  color: ${color.navy};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
`;

const Words = styled.span`
  flex: 1;
  min-width: ${wordsMinWidth}px;
`;

const Source = styled.strong`
  font-family: ${typeScale.labelValue.fontFamily};
  font-weight: normal;
`;

const KeyGap = styled.span`
  margin-left: ${spaceMd}px;
`;

// Escape acts as Back unless the admin is typing or choosing in a field (ACCT-R19).
const isTypingTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;

  return ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
};

const useEscapeToGoBack = (goBack: () => void) => {
  useEffect(() => {
    const goBackOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (isTypingTarget(event.target)) return;

      goBack();
    };

    document.addEventListener("keydown", goBackOnEscape);

    return () => document.removeEventListener("keydown", goBackOnEscape);
  }, [goBack]);
};

// The bar on top of an account opened from a row of Case Search or Order Search: where it came
// from, and the way back to those results (ACCT-R17…R19).
export const OpenedFromBar = ({ origin }: { origin: SearchOrigin }) => {
  const isPhone = useIsPhoneLayout();
  const history = useHistory();
  const { source, picked, backLabel, backAddress } = describeSearchOrigin(origin);
  const searchName = origin.search === "case" ? "Case Search" : "Order Search";

  const goBack = useCallback(() => history.push(backAddress), [history, backAddress]);
  useEscapeToGoBack(goBack);

  return (
    <Bar $isPhone={isPhone} aria-label={`Opened from ${searchName}`}>
      <Words>
        Opened from <Source>{source}</Source>. Everything on file for this User is below; the{" "}
        {picked} you picked is highlighted and marked “Searched”.
      </Words>
      <Button look="secondary" onClick={goBack}>
        {backLabel}
        <KeyGap aria-hidden="true">
          <Key>Esc</Key>
        </KeyGap>
      </Button>
    </Bar>
  );
};
